import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { LibraryManager, ResourceStatus, ResourceType } from 'libs.library_manager';
import { useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { ApiClientService, AuthContext, useApiClient } from 'libs.react_internal';
import { getNextCloneName } from '../../../utils/clone_name.utils';
import { deduplicateResourcesByKey } from '../../../utils/deduplicate_by_key.utils';
/**
 * Hook for managing chart resources.
 * Phase 1: Uses local state with mock data.
 * Phase 2: Will be replaced with real LibraryManager API calls.
 */
export const useCharts = () => {
    const { entityId } = useContext(AuthContext);
    const libraryClient = useApiClient(ApiClientService.LIBRARY);
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const [charts, setCharts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    // Refs to avoid callback recreation
    const libraryClientRef = useRef(libraryClient);
    const entityIdRef = useRef(entityId);
    const translateRef = useRef(translate);
    const addNotificationRef = useRef(addNotification);
    useEffect(() => {
        libraryClientRef.current = libraryClient;
        entityIdRef.current = entityId;
        translateRef.current = translate;
        addNotificationRef.current = addNotification;
    }, [libraryClient, entityId, translate, addNotification]);
    /** Fetch all charts, optionally including archived */
    const fetchCharts = useCallback(async (showArchived = false, status, search) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const response = await libraryManager.getResources(ResourceType.REPORTING_CHART, {
                entityId: entityIdRef.current,
                active: showArchived ? 'false' : 'true',
                defaultLibrary: true,
                ...(status ? { status } : {}),
                ...(search ? { search } : {}),
            });
            const result = response.data || [];
            // SSP-4186: collapse `key` chains to a single row (highest version). Mirrors
            // the same fix in useDashboards. Archive view keeps every row for auditing.
            setCharts(showArchived ? result : deduplicateResourcesByKey(result));
        }
        catch (error) {
            addNotificationRef.current({
                title: translateRef.current('Error fetching charts'),
                subtitle: translateRef.current('Failed to load charts'),
                type: 'error',
            });
            setCharts([]);
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    /** Fetch a single chart by ID */
    const fetchChartById = useCallback(async (chartId) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const resource = await libraryManager.getResourceById(chartId, ResourceType.REPORTING_CHART);
            return resource;
        }
        catch (error) {
            addNotificationRef.current({
                title: translateRef.current('Error fetching chart'),
                subtitle: translateRef.current('Failed to load chart details'),
                type: 'error',
            });
            return undefined;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    /** Create a new chart */
    const createChart = useCallback(async (form) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const createRequest = {
                name: form.name,
                type: ResourceType.REPORTING_CHART,
                status: ResourceStatus.DRAFT,
                entityId: entityIdRef.current?.toString(),
                data: {
                    title: '',
                    type: 'table',
                    query: { dimensions: [], measures: [] },
                    cubeConfigId: form.cubeConfigId,
                    cubeConfigKey: form.cubeConfigKey,
                },
                metadata: {
                    title: form.name,
                    description: form.description || '',
                },
            };
            const resource = await libraryManager.createResource(createRequest, ResourceType.REPORTING_CHART);
            const newChart = resource;
            addNotificationRef.current({
                title: translateRef.current('Chart created successfully'),
                type: 'success',
            });
            setCharts((prev) => [...prev, newChart]);
            return newChart;
        }
        catch (error) {
            addNotificationRef.current({
                title: translateRef.current('Error creating chart'),
                subtitle: translateRef.current('Failed to create chart'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    /**
     * Clone an existing chart.
     *
     * When `options.silent` is true the hook suppresses its built-in success/error
     * toasts. This is used by compound flows (SSP-4186) that need to emit a single
     * outcome toast covering both the clone and a follow-up archive.
     */
    const cloneChart = useCallback(async (sourceChart, option = 'CLONE', overrides, options) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const clonedName = overrides?.name || sourceChart.name;
            const clonedDescription = overrides?.description ?? sourceChart.metadata?.description ?? '';
            const createRequest = {
                name: clonedName,
                type: ResourceType.REPORTING_CHART,
                status: ResourceStatus.DRAFT,
                entityId: entityIdRef.current?.toString(),
                data: {
                    ...(sourceChart.data || {}),
                    ...(overrides?.cubeConfigId ? { cubeConfigId: overrides.cubeConfigId } : {}),
                    ...(overrides?.cubeConfigKey ? { cubeConfigKey: overrides.cubeConfigKey } : {}),
                },
                metadata: {
                    title: clonedName,
                    description: clonedDescription,
                },
            };
            // For CREATE_VERSION, link to the same version chain via key
            if (option === 'CREATE_VERSION') {
                createRequest.key = sourceChart.key || sourceChart.id;
            }
            const resource = await libraryManager.createResource(createRequest, ResourceType.REPORTING_CHART);
            const cloned = resource;
            if (!options?.silent) {
                addNotificationRef.current({
                    title: translateRef.current('Chart cloned successfully'),
                    type: 'success',
                });
            }
            // SSP-4186: update-or-append by id so a follow-up fetch that already
            // returned the new resource does not double it in local state.
            setCharts((prev) => (prev.some((c) => c.id === cloned.id) ? prev : [...prev, cloned]));
            return cloned;
        }
        catch (error) {
            if (!options?.silent) {
                addNotificationRef.current({
                    title: translateRef.current('Error cloning chart'),
                    subtitle: translateRef.current('Failed to clone chart'),
                    type: 'error',
                });
            }
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    /** Update chart details (name, description, dataset) — persists via LibraryManager */
    const updateChartDetails = useCallback(async (chartId, form) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            // Fetch existing chart to preserve all data fields (PUT replaces entire object)
            const existing = await libraryManager.getResourceById(chartId, ResourceType.REPORTING_CHART);
            const existingData = existing?.data || {};
            const datasetChanged = form.cubeConfigId !== existingData.cubeConfigId;
            // When the dataset changes, clear all fields that reference the old cube schema
            const updatedData = datasetChanged
                ? {
                    title: existingData.title || '',
                    type: existingData.type || 'table',
                    query: { dimensions: [], measures: [] },
                    cardInfo: existingData.cardInfo,
                    cubeConfigId: form.cubeConfigId,
                    cubeConfigKey: form.cubeConfigKey,
                }
                : {
                    ...existingData,
                    cubeConfigId: form.cubeConfigId,
                    cubeConfigKey: form.cubeConfigKey,
                };
            const resource = await libraryManager.updateResource(chartId, ResourceType.REPORTING_CHART, {
                name: form.name,
                metadata: { title: form.name, description: form.description },
                data: updatedData,
            });
            const updated = resource;
            // Update local cache
            setCharts((prev) => prev.map((c) => (c.id === chartId ? updated : c)));
            addNotificationRef.current({
                title: translateRef.current('Chart details updated'),
                type: 'success',
            });
            return updated;
        }
        catch (error) {
            addNotificationRef.current({
                title: translateRef.current('Error updating chart details'),
                subtitle: translateRef.current('Failed to update chart details'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    /** Update chart data config (from settings panel) — persists via LibraryManager */
    const updateChartData = useCallback(async (chartId, data) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const existing = await libraryManager.getResourceById(chartId, ResourceType.REPORTING_CHART);
            const existingData = (existing?.data || {});
            const resource = await libraryManager.updateResource(chartId, ResourceType.REPORTING_CHART, {
                data: {
                    ...existingData,
                    ...data,
                },
            });
            const updated = resource;
            // Update local cache
            setCharts((prev) => prev.map((c) => (c.id === chartId ? updated : c)));
            addNotificationRef.current({
                title: translateRef.current('Chart saved successfully'),
                type: 'success',
            });
            return updated;
        }
        catch (error) {
            addNotificationRef.current({
                title: translateRef.current('Error saving chart'),
                subtitle: translateRef.current('Failed to save chart configuration'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    /** Transition chart status */
    const transitionStatus = useCallback(async (chartId, newStatus) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const resource = await libraryManager.updateResource(chartId, ResourceType.REPORTING_CHART, {
                status: newStatus,
            });
            const updated = resource;
            // Update local cache
            setCharts((prev) => prev.map((c) => (c.id === chartId ? updated : c)));
            addNotificationRef.current({
                title: translateRef.current('Chart status updated'),
                type: 'success',
            });
            return updated;
        }
        catch (error) {
            addNotificationRef.current({
                title: translateRef.current('Error updating chart status'),
                subtitle: translateRef.current('Failed to change chart status'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    /**
     * Archive a chart.
     *
     * When `options.silent` is true the hook suppresses its built-in success/error
     * toasts so compound flows (SSP-4186) can emit a single summary toast.
     */
    const archiveChart = useCallback(async (chartId, options) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            await libraryManager.archiveResource(chartId, ResourceType.REPORTING_CHART);
            // Update local cache
            setCharts((prev) => prev.map((c) => (c.id === chartId ? { ...c, active: false } : c)));
            if (!options?.silent) {
                addNotificationRef.current({
                    title: translateRef.current('Chart archived successfully'),
                    type: 'success',
                });
            }
        }
        catch (error) {
            if (!options?.silent) {
                addNotificationRef.current({
                    title: translateRef.current('Error archiving chart'),
                    subtitle: translateRef.current('Failed to archive chart'),
                    type: 'error',
                });
            }
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    /** Check if a chart name is unique */
    const checkChartNameUnique = useCallback(async (nameToCheck, excludeId) => {
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const response = await libraryManager.getResources(ResourceType.REPORTING_CHART, {
                entityId: entityIdRef.current,
                active: 'true',
                name: nameToCheck,
            });
            // The API's `name` param does a partial match, so we still need an
            // exact case-insensitive check on the narrowed-down result set.
            const matches = response.data || [];
            const duplicate = matches.find((c) => c.name.toLowerCase() === nameToCheck.toLowerCase() && c.id !== excludeId);
            return !duplicate;
        }
        catch {
            // If fetch fails, assume unique to allow user to proceed
            return true;
        }
    }, []);
    /** Fetch available datasets (cube configs) for the dropdown — only PUBLISHED (Ready) ones */
    const fetchDatasets = useCallback(async () => {
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const response = await libraryManager.getResources(ResourceType.CUBE_CONFIG, {
                entityId: entityIdRef.current,
                active: 'true',
                status: ResourceStatus.PUBLISHED,
            });
            const datasets = response.data || [];
            return datasets.map((ds) => ({
                id: ds.id,
                key: ds.key,
                name: ds.name,
                status: ds.status,
            }));
        }
        catch (error) {
            addNotificationRef.current({
                title: translateRef.current('Error fetching datasets'),
                subtitle: translateRef.current('Failed to load available datasets'),
                type: 'error',
            });
            return [];
        }
    }, []);
    /** Generate a unique clone name by querying existing charts with a partial name match */
    const generateUniqueCloneName = useCallback(async (baseName) => {
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const response = await libraryManager.getResources(ResourceType.REPORTING_CHART, {
                entityId: entityIdRef.current,
                active: 'true',
                name: baseName,
            });
            const existingNames = (response.data || []).map((c) => c.name);
            return getNextCloneName(baseName, existingNames);
        }
        catch {
            return `${baseName} (Clone)`;
        }
    }, []);
    return {
        charts,
        isLoading,
        fetchCharts,
        fetchChartById,
        createChart,
        cloneChart,
        updateChartDetails,
        updateChartData,
        transitionStatus,
        archiveChart,
        checkChartNameUnique,
        generateUniqueCloneName,
        fetchDatasets,
    };
};
